import React, { useContext, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import OrdenoLoading from '../../../../components/OrdenoLoading'
import {
  ModalOverlay,
  DocumentContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ScrollableContent,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoItemWithTooltip,
  InfoValueContainer,
  InfoLink,
  InfoLabel,
  InfoValue,
  ItemCard,
  ItemHeader,
  ItemContent,
  ServicesList,
  ServiceItem,
  SubItemTypeTag,
  AttachmentsList,
  AttachmentItem,
  MessageContainer,
  ErrorMessage,
  Table,
  TableHeaderCell,
  TableCell,
  TableRow,
} from './style'
import { Info } from 'lucide-react'
import { fetchOrderById } from './api'
import { OrderSchemaType } from '../AddOrder/order.schema'
import { paymentMethodLabel } from '../AddOrder/constants'
import ModalContext from '../../../../contexts/ModalContext'
import InfoTooltip from '../../../../components/InfoTooltip'

const ViewSteelOrder: React.FC = () => {
  const { setModalActive } = useContext(ModalContext)
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [activeInfo, setActiveInfo] = useState<string | null>(null)
  const [collapsedItems, setCollapsedItems] = useState<{
    [key: number]: boolean
  }>({})

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useQuery<OrderSchemaType>({
    queryKey: ['steelOrder', orderId],
    queryFn: () => fetchOrderById(orderId!),
    enabled: !!orderId,
  })

  useEffect(() => {
    if (order?.items) {
      const initialCollapsedState: { [key: number]: boolean } = {}
      order.items.forEach((_, index) => {
        initialCollapsedState[index] = true
      })
      setCollapsedItems(initialCollapsedState)
    }
  }, [order])

  const handleClose = () => {
    setModalActive(false)
    navigate('/panel/steel')
  }

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return
    setActiveInfo(activeInfo === type ? null : type)
  }

  const toggleItemCollapsed = (index: number) => {
    setCollapsedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const getItemTypeCounts = (item: any) => {
    const counts = { P: 0, S: 0, I: 0, M: 0 }

    if (item.itemType === 'PRODUCT') counts.P = 1
    if (item.itemType === 'SERVICE') counts.S = 1
    if (item.itemType === 'RESOURCE') counts.I = 1
    if (item.itemType === 'MATERIAL') counts.M = 1

    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subItem: any) => {
        if (subItem.itemType === 'PRODUCT') counts.P++
        if (subItem.itemType === 'SERVICE') counts.S++
        if (subItem.itemType === 'RESOURCE') counts.I++
        if (subItem.itemType === 'MATERIAL') counts.M++
      })
    }

    return counts
  }

  const buildAddressString = (entity: any): string => {
    const safe = (v: any): string => {
      if (!v && v !== 0) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'object' && v !== null) return v.name || v.label || '';
      return '';
    };
    const street = safe(entity.street);
    const number = safe(entity.number);
    const neighborhood = safe(entity.neighborhood);
    const city = safe(entity.city);
    const state = safe(entity.state);
    const rawCep = entity.cep || '';
    const cepDigits = String(rawCep).replace(/\D/g, '');
    const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : '';
    const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : '';
    const parts: string[] = [];
    if (street) parts.push(`${street}${number ? `, ${number}` : ''}`);
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    const main = parts.join(' - ');
    const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main;
    return result.trim();
  };

  const getSupplierDetails = () => {
    if (!order?.supplier || order.supplier === 'Não informado') return undefined
    const supplier = order?.supplierDetails
    if (!supplier) return undefined
    const address = buildAddressString(supplier)
    const tipo = supplier.isMain ? (supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return {
      'Razão Social': supplier.companyName || supplier.name || 'Não informado',
      ...(tipo ? { 'Tipo': tipo } : {}),
      'CNPJ/CPF': supplier.cnpj || supplier.cpf || 'Não informado',
      'Nome do Contato': supplier.contactName || supplier.contato || (supplier.contacts?.[0]?.name) || 'Não informado',
      'Email': supplier.email || (supplier.contacts?.[0]?.email) || 'Não informado',
      'Telefone': supplier.phone || supplier.telefone || (supplier.contacts?.[0]?.phoneNumber) || 'Não informado',
      ...(address ? { 'Endereço': address } : {}),
    }
  }

  const getSellerDetails = () => {
    if (!order?.seller || order.seller === 'Não informado') return undefined
    const seller = order?.sellerDetails
    if (!seller) return undefined
    return {
      'Nome': seller.name || 'Não informado',
      'Email': seller.email || 'Não informado',
      'Telefone': seller.phone || seller.telefone || 'Não informado',
    }
  }

  const getClosedByDetails = () => {
    if (!order?.closedBy || order.closedBy === 'Não informado') return undefined
    const closedBy = order?.closedByDetails
    if (!closedBy) return undefined
    return {
      'Nome': closedBy.name || 'Não informado',
      'Email': closedBy.email || 'Não informado',
      'Telefone': closedBy.phone || closedBy.telefone || 'Não informado',
    }
  }

  const getClientDetails = () => {
    if (!order?.client || order.client === 'Não informado') return undefined
    const client = order?.clientDetails
    if (!client) return undefined
    const address = buildAddressString(client)
    const tipo = client.isMain ? (client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return {
      'Razão Social': client.companyName || client.name || 'Não informado',
      ...(tipo ? { 'Tipo': tipo } : {}),
      'CNPJ/CPF': client.cnpj || client.cpf || 'Não informado',
      'Nome do Contato': client.contactName || client.contato || (client.contacts?.[0]?.name) || 'Não informado',
      'Email': client.email || (client.contacts?.[0]?.email) || 'Não informado',
      'Telefone': client.phone || client.telefone || (client.contacts?.[0]?.phoneNumber) || 'Não informado',
      ...(address ? { 'Endereço': address } : {}),
    }
  }

  const formatCurrency = (value?: number) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Não informado'
    // Datas date-only (YYYY-MM-DD ou meia-noite UTC): montar direto — o parse via Date
    // interpreta como UTC e recua um dia no fuso local.
    const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Campos que chegam como string legada ou como objeto (User/Company/lookup) — nunca renderizar cru.
  const displayUser = (v: any): string =>
    typeof v === 'object' && v !== null
      ? v.name || v.companyName || 'Não informado'
      : v || 'Não informado'

  // As relações do payload chegam em PascalCase (Prisma); respostas legadas/mapeadas usam camelCase.
  const rel = (source: any, ...keys: string[]): any => {
    for (const key of keys) {
      const value = source?.[key]
      if (value !== undefined && value !== null) return value
    }
    return undefined
  }

  const itemRefs = (entry: any) => ({
    material: rel(entry, 'Material', 'material'),
    product: rel(entry, 'Product', 'product'),
    stage: rel(entry, 'Stage', 'stage'),
    resource: rel(entry, 'Resource', 'resource'),
    stock: rel(entry, 'Stock', 'stock'),
    script: rel(entry, 'Script', 'script'),
    ncm: rel(entry, 'Ncm', 'ncm'),
    unit: rel(entry, 'UnitOfMeasurement', 'unitOfMeasurement'),
    serviceCategory: rel(entry, 'Category_Stage', 'serviceCategory'),
    serviceType: rel(entry, 'Type_Category_Stage', 'serviceType'),
    serviceSupplier: rel(
      entry,
      'Company_OrderItem_serviceSupplierCompanyIdToCompany',
      'serviceSupplierCompany',
    ),
    resourceCategory: rel(entry, 'Category_Resource', 'resourceCategory'),
    resourceType: rel(entry, 'Type_Category_Resource', 'resourceType'),
    resourceSupplier: rel(
      entry,
      'Company_OrderItem_resourceSupplierCompanyIdToCompany',
      'resourceSupplierCompany',
    ),
  })

  const stockDetails = (stock: any) => ({
    description: stock?.description,
    code: rel(stock, 'Code', 'code')?.code,
    fabrication: rel(stock, 'Fabrication', 'fabrication')?.type,
    format: rel(stock, 'Format', 'format')?.type,
    heatTreatment: rel(stock, 'HeatTreatment', 'heatTreatment')?.type,
    originCountry: rel(stock, 'OriginCountries', 'originCountry')?.countryName,
    // Stock↔Company é join (Company_Stock[]) no schema; não existe relação Company direta.
    supplier:
      stock?.Company_Stock?.[0]?.Company?.companyName ||
      stock?.supplier?.companyName,
  })

  const attachmentNames = (entry: any): string =>
    (entry?.Attachments || [])
      .map((a: any) => a.GeneralFile?.title || a.GeneralFile?.fileUrl || '')
      .filter(Boolean)
      .join(', ')

  const getSupplierNameWithLabel = () => {
    if (!order) return 'Não informado';
    const supplierName = displayUser(order.supplier);
    if (supplierName === 'Não informado') return 'Não informado';
    const supplier = order.supplierDetails;
    if (!supplier) return supplierName;
    const tipo = supplier.isMain ? (supplier.tipoEstabelecimento || (supplier.cnpj ? (supplier.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return tipo ? `${supplierName} - ${tipo}` : supplierName;
  };

  const getClientNameWithLabel = () => {
    if (!order) return 'Não informado';
    const clientName = displayUser(order.client);
    if (clientName === 'Não informado') return 'Não informado';
    const client = order.clientDetails;
    if (!client) return clientName;
    const tipo = client.isMain ? (client.tipoEstabelecimento || (client.cnpj ? (client.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')) : '';
    return tipo ? `${clientName} - ${tipo}` : clientName;
  };

  if (isLoading) {
    return (
      <DocumentContainer>
        <OrdenoLoading variant="inline" text="Carregando detalhes do pedido..." style={{ padding: '4rem 0' }} />
      </DocumentContainer>
    )
  }

  if (isError) {
    return (
      <DocumentContainer>
        <ErrorMessage>
          Falha ao carregar o pedido. Erro:{' '}
          {error?.message || 'Erro desconhecido'}
        </ErrorMessage>
      </DocumentContainer>
    )
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <DocumentContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Detalhes do Pedido - Nº {order?.id}</ModalTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        <ScrollableContent>
          <SectionTitle>Informações Gerais</SectionTitle>
          <InfoGrid>
            <InfoItemWithTooltip>
              <InfoLabel>Fornecedor:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{getSupplierNameWithLabel()}</InfoValue>
                <InfoLink
                  disabled={
                    !order?.supplier || order.supplier === 'Não informado'
                  }
                  onClick={() =>
                    handleInfoToggle(
                      'supplier',
                      !!(order?.supplier && order.supplier !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'supplier' &&
                (getSupplierDetails() ? (
                  <InfoTooltip details={getSupplierDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do fornecedor.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            <InfoItemWithTooltip>
              <InfoLabel>Vendedor:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{displayUser(order?.seller)}</InfoValue>
                <InfoLink
                  disabled={!order?.seller || order.seller === 'Não informado'}
                  onClick={() =>
                    handleInfoToggle(
                      'seller',
                      !!(order?.seller && order.seller !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'seller' &&
                (getSellerDetails() ? (
                  <InfoTooltip details={getSellerDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do vendedor.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            <InfoItemWithTooltip>
              <InfoLabel>Cliente:</InfoLabel>
              <InfoValueContainer>
                <InfoValue>{getClientNameWithLabel()}</InfoValue>
                <InfoLink
                  disabled={!order?.client || order.client === 'Não informado'}
                  onClick={() =>
                    handleInfoToggle(
                      'client',
                      !!(order?.client && order.client !== 'Não informado'),
                    )
                  }
                >
                  <Info size={16} />
                  <span>Mais informações</span>
                </InfoLink>
              </InfoValueContainer>
              {activeInfo === 'client' &&
                (getClientDetails() ? (
                  <InfoTooltip details={getClientDetails()!} />
                ) : (
                  <ErrorMessage>
                    Não foi possível encontrar os detalhes do cliente.
                  </ErrorMessage>
                ))}
            </InfoItemWithTooltip>

            {order?.status === true && order?.closedBy && (
              <InfoItemWithTooltip>
                <InfoLabel>Fechado por:</InfoLabel>
                <InfoValueContainer>
                  <InfoValue>{displayUser(order.closedBy)}</InfoValue>
                  <InfoLink
                    disabled={!order.closedBy || order.closedBy === 'Não informado'}
                    onClick={() =>
                      handleInfoToggle(
                        'closedBy',
                        !!(order.closedBy && order.closedBy !== 'Não informado'),
                      )
                    }
                  >
                    <Info size={16} />
                    <span>Mais informações</span>
                  </InfoLink>
                </InfoValueContainer>
                {activeInfo === 'closedBy' &&
                  (getClosedByDetails() ? (
                    <InfoTooltip details={getClosedByDetails()!} />
                  ) : (
                    <ErrorMessage>
                      Não foi possível encontrar os detalhes do usuário.
                    </ErrorMessage>
                  ))}
              </InfoItemWithTooltip>
            )}

            <InfoItem>
              <InfoLabel>Tipo de Pedido:</InfoLabel>
              <InfoValue>{displayUser((order as any)?.orderType ?? (order as any)?.OrderType)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Data do Pedido:</InfoLabel>
              <InfoValue>{formatDate(order?.orderDate)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Data de Entrega:</InfoLabel>
              <InfoValue>{formatDate(order?.deliveryDate)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Condição de Pagamento:</InfoLabel>
              <InfoValue>
                {(order as any)?.PaymentCondition?.name || (order as any)?.paymentCondition?.name || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Forma de Pagamento:</InfoLabel>
              <InfoValue>
                {paymentMethodLabel((order as any)?.OrderPaymentMethod?.[0]?.PaymentMethod?.name) || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Tipo de Frete:</InfoLabel>
              <InfoValue>
                {(order as any)?.ShippingType?.name || (order as any)?.shippingType?.name || 'Não informado'}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Valor do Frete:</InfoLabel>
              <InfoValue>{formatCurrency(order?.shippingValue)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Código de Rastreamento:</InfoLabel>
              <InfoValue>{order?.trackingCode || 'Não informado'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Situação:</InfoLabel>
              <InfoValue>
                {displayUser((order as any)?.orderSituation ?? (order as any)?.OrderSituation)}
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          {order?.extraInfo && (
            <>
              <div style={{ marginBottom: '50px' }}>
                <SectionTitle>Informações Adicionais</SectionTitle>
                <div style={{ marginTop: '40px' }}>
                  <InfoValue className='extra-info-block'>
                    {order.extraInfo}
                  </InfoValue>
                </div>
              </div>
            </>
          )}

          {order?.attachments && order.attachments.length > 0 && (
            <>
              <div style={{ marginTop: '25px' }}>
                <SectionTitle>Anexos</SectionTitle>
                <AttachmentsList>
                  {order.attachments.map((attachment: any, index: number) => (
                    <AttachmentItem key={index}>
                      📎 {typeof attachment === 'string' ? attachment : attachment?.title || attachment?.fileUrl || 'Anexo'}
                    </AttachmentItem>
                  ))}
                </AttachmentsList>
              </div>
            </>
          )}

          <SectionTitle>Itens do Pedido</SectionTitle>
          {order?.items?.map((item: any, index: number) => {
            const refs = itemRefs(item)
            const stock = stockDetails(refs.stock)
            const unitName = refs.unit?.name || item.unitOfMeasurementName
            const itemAttachments = attachmentNames(item)

            const sellingPrice = Number(item.sellingPrice) || 0
            const itemPrice = Number(item.itemPrice) || 0
            // Mesma regra do formulário de venda: o praticado manda; sem ele, vale o cadastrado.
            const displayPrice = sellingPrice > 0 ? sellingPrice : itemPrice
            const priceKgMin = Number(item.priceKgMin) || 0
            const priceKg = Number(item.priceKg) || 0
            const discount = Number(item.descount) || 0
            const hasBusinessDays =
              item.deliveryBusinessDays !== null &&
              item.deliveryBusinessDays !== undefined &&
              item.deliveryBusinessDays !== ''

            const getItemTitle = () => {
              if (item.itemType === 'MATERIAL' && item.material?.name) {
                return item.material.name
              }
              if (item.itemType === 'PRODUCT' && item.product?.name) {
                return item.product.name
              }
              if (item.itemType === 'SERVICE' && item.stage?.name) {
                return item.stage.name
              }
              if (item.itemType === 'RESOURCE' && item.resource?.name) {
                return item.resource.name
              }
              return `${item.itemType} ${index + 1}`
            }

            const getItemSubtitle = () => {
              if (item.itemType === 'SERVICE' && item.serviceCategory?.name) {
                return ` - ${item.serviceCategory.name}`
              }
              if (item.itemType === 'RESOURCE' && item.resourceCategory?.name) {
                return ` - ${item.resourceCategory.name}`
              }
              if (item.itemType === 'MATERIAL' && item.stock?.description) {
                return ` - ${item.stock.description}`
              }
              return ''
            }

            const getItemTypeLabel = () => {
              const typeMap: { [key: string]: string } = {
                MATERIAL: 'Material',
                PRODUCT: 'Produto',
                SERVICE: 'Serviço',
                RESOURCE: 'Insumo',
              }
              return typeMap[item.itemType] || item.itemType
            }

            return (
              <ItemCard key={index}>
                <ItemHeader
                  onClick={() => toggleItemCollapsed(index)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        color: 'white',
                      }}
                    >
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Item Principal:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {getItemTypeLabel()}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Quantidade de produtos:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {1 + (item.subItems?.length || 0)}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Totais por tipo de produto:
                      </span>
                      {(() => {
                        const counts = getItemTypeCounts(item)
                        const typeLabels = []
                        if (counts.P > 0) typeLabels.push(`Produto:${counts.P}`)
                        if (counts.S > 0) typeLabels.push(`Serviço:${counts.S}`)
                        if (counts.I > 0) typeLabels.push(`Insumo:${counts.I}`)
                        if (counts.M > 0)
                          typeLabels.push(`Material:${counts.M}`)

                        if (typeLabels.length === 0) return null
                        if (typeLabels.length === 1)
                          return (
                            <span style={{ fontSize: '14px' }}>
                              {typeLabels[0]}
                            </span>
                          )
                        if (typeLabels.length === 2)
                          return (
                            <span style={{ fontSize: '14px' }}>
                              {typeLabels[0]} e {typeLabels[1]}
                            </span>
                          )

                        const lastType = typeLabels.pop()
                        return (
                          <span style={{ fontSize: '14px' }}>
                            {typeLabels.join(', ')} e {lastType}
                          </span>
                        )
                      })()}
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Qtd.:
                      </span>
                      <span style={{ fontSize: '14px' }}>
                        {item.quantity || 0}
                        {unitName ? ` ${unitName}` : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: '20px', color: 'white' }}>
                      {collapsedItems[index] ? '▼' : '▶'}
                    </div>
                  </div>
                </ItemHeader>
                {!collapsedItems[index] && (
                  <ItemContent>
                    {item.itemType === 'MATERIAL' && (
                      <>
                        <h5>⚙️ Material</h5>
                        <InfoGrid>
                          {refs.material && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {refs.material.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {stock.description && (
                            <InfoItem>
                              <InfoLabel>Estoque:</InfoLabel>
                              <InfoValue>{stock.description}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.code && (
                            <InfoItem>
                              <InfoLabel>Código:</InfoLabel>
                              <InfoValue>{stock.code}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.fabrication && (
                            <InfoItem>
                              <InfoLabel>Fabricação:</InfoLabel>
                              <InfoValue>{stock.fabrication}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.format && (
                            <InfoItem>
                              <InfoLabel>Formato:</InfoLabel>
                              <InfoValue>{stock.format}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.heatTreatment && (
                            <InfoItem>
                              <InfoLabel>Tratamento Térmico:</InfoLabel>
                              <InfoValue>{stock.heatTreatment}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.originCountry && (
                            <InfoItem>
                              <InfoLabel>Origem:</InfoLabel>
                              <InfoValue>{stock.originCountry}</InfoValue>
                            </InfoItem>
                          )}
                          {stock.supplier && (
                            <InfoItem>
                              <InfoLabel>Fornecedor:</InfoLabel>
                              <InfoValue>{stock.supplier}</InfoValue>
                            </InfoItem>
                          )}
                          {refs.ncm?.ncm && (
                            <InfoItem>
                              <InfoLabel>NCM:</InfoLabel>
                              <InfoValue>{refs.ncm.ncm}</InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {itemAttachments && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>{itemAttachments}</InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'SERVICE' && (
                      <>
                        <h5>🔧 Serviço</h5>
                        <InfoGrid>
                          {refs.stage && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>{refs.stage.name || 'N/A'}</InfoValue>
                            </InfoItem>
                          )}
                          {refs.serviceCategory && (
                            <InfoItem>
                              <InfoLabel>Categoria:</InfoLabel>
                              <InfoValue>
                                {refs.serviceCategory.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {refs.serviceType && (
                            <InfoItem>
                              <InfoLabel>Tipo:</InfoLabel>
                              <InfoValue>
                                {refs.serviceType.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {refs.serviceSupplier && (
                            <InfoItem>
                              <InfoLabel>Fornecedor:</InfoLabel>
                              <InfoValue>
                                {refs.serviceSupplier.companyName || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {itemAttachments && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>{itemAttachments}</InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'RESOURCE' && (
                      <>
                        <h5>🔧 Insumo</h5>
                        <InfoGrid>
                          {refs.resource && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {refs.resource.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {refs.resourceCategory && (
                            <InfoItem>
                              <InfoLabel>Categoria:</InfoLabel>
                              <InfoValue>
                                {refs.resourceCategory.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {refs.resourceType && (
                            <InfoItem>
                              <InfoLabel>Tipo:</InfoLabel>
                              <InfoValue>
                                {refs.resourceType.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {refs.resourceSupplier && (
                            <InfoItem>
                              <InfoLabel>Fornecedor:</InfoLabel>
                              <InfoValue>
                                {refs.resourceSupplier.companyName || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {itemAttachments && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>{itemAttachments}</InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.itemType === 'PRODUCT' && (
                      <>
                        <h5>📦 Produto</h5>
                        <InfoGrid>
                          {refs.product && (
                            <InfoItem>
                              <InfoLabel>Nome:</InfoLabel>
                              <InfoValue>
                                {refs.product.name || 'N/A'}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {item.nomeCustomizado && (
                            <InfoItem>
                              <InfoLabel>Especificação:</InfoLabel>
                              <InfoValue>{item.nomeCustomizado}</InfoValue>
                            </InfoItem>
                          )}
                          {refs.script && (
                            <InfoItem>
                              <InfoLabel>Script:</InfoLabel>
                              <InfoValue>{refs.script.name || 'N/A'}</InfoValue>
                            </InfoItem>
                          )}
                          <InfoItem>
                            <InfoLabel>Quantidade:</InfoLabel>
                            <InfoValue>{item.quantity || 'N/A'}</InfoValue>
                          </InfoItem>
                          {itemAttachments && (
                            <InfoItem>
                              <InfoLabel>Arquivo:</InfoLabel>
                              <InfoValue>{itemAttachments}</InfoValue>
                            </InfoItem>
                          )}
                          {item.extraInfo && (
                            <InfoItem>
                              <InfoLabel>Observações:</InfoLabel>
                              <InfoValue>{item.extraInfo}</InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>
                      </>
                    )}

                    {item.subItems && item.subItems.length > 0 && (
                      <>
                        {item.subItems.map((subItem: any, subIndex: number) => {
                          const subRefs = itemRefs(subItem)
                          const subStock = stockDetails(subRefs.stock)
                          const subAttachments = attachmentNames(subItem)
                          const subPrice = Number(subItem.itemPrice) || 0

                          const getSubItemTypeLabel = () => {
                            const typeMap: { [key: string]: string } = {
                              MATERIAL: 'Matéria Prima',
                              PRODUCT: 'Produto Final',
                              SERVICE: 'Serviço',
                              RESOURCE: 'Insumo',
                            }
                            return typeMap[subItem.itemType] || subItem.itemType
                          }

                          const getSubItemTitle = () => {
                            const name =
                              subRefs.material?.name ||
                              subRefs.product?.name ||
                              subRefs.stage?.name ||
                              subRefs.resource?.name
                            return (
                              name || `${getSubItemTypeLabel()} ${subIndex + 1}`
                            )
                          }

                          const subItemTitle = (emoji: string) => (
                            <h5
                              style={{
                                marginBottom: '15px',
                                color: '#333',
                                fontSize: '16px',
                              }}
                            >
                              {emoji} {getSubItemTitle()}
                              <SubItemTypeTag>
                                {getSubItemTypeLabel()}
                              </SubItemTypeTag>
                            </h5>
                          )

                          const subItemCommonInfo = (
                            <>
                              <InfoItem>
                                <InfoLabel>Quantidade:</InfoLabel>
                                <InfoValue>
                                  {subItem.quantity || 'N/A'}
                                </InfoValue>
                              </InfoItem>
                              {subPrice > 0 && (
                                <InfoItem>
                                  <InfoLabel>Valor no pedido:</InfoLabel>
                                  <InfoValue>
                                    {formatCurrency(subPrice)}
                                  </InfoValue>
                                </InfoItem>
                              )}
                              {subAttachments && (
                                <InfoItem>
                                  <InfoLabel>Arquivo:</InfoLabel>
                                  <InfoValue>{subAttachments}</InfoValue>
                                </InfoItem>
                              )}
                              {subItem.extraInfo && (
                                <InfoItem>
                                  <InfoLabel>Observações:</InfoLabel>
                                  <InfoValue>{subItem.extraInfo}</InfoValue>
                                </InfoItem>
                              )}
                            </>
                          )

                          return (
                            <div
                              key={subIndex}
                              style={{
                                marginLeft: '20px',
                                borderLeft: '2px solid #e0e0e0',
                                paddingLeft: '15px',
                                marginTop: '15px',
                              }}
                            >
                              {subItem.itemType === 'MATERIAL' && (
                                <>
                                  {subItemTitle('⚙️')}
                                  <InfoGrid>
                                    {subStock.description && (
                                      <InfoItem>
                                        <InfoLabel>Estoque:</InfoLabel>
                                        <InfoValue>
                                          {subStock.description}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.code && (
                                      <InfoItem>
                                        <InfoLabel>Código:</InfoLabel>
                                        <InfoValue>{subStock.code}</InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.fabrication && (
                                      <InfoItem>
                                        <InfoLabel>Fabricação:</InfoLabel>
                                        <InfoValue>
                                          {subStock.fabrication}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.format && (
                                      <InfoItem>
                                        <InfoLabel>Formato:</InfoLabel>
                                        <InfoValue>{subStock.format}</InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.heatTreatment && (
                                      <InfoItem>
                                        <InfoLabel>
                                          Tratamento Térmico:
                                        </InfoLabel>
                                        <InfoValue>
                                          {subStock.heatTreatment}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.originCountry && (
                                      <InfoItem>
                                        <InfoLabel>Origem:</InfoLabel>
                                        <InfoValue>
                                          {subStock.originCountry}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subStock.supplier && (
                                      <InfoItem>
                                        <InfoLabel>Fornecedor:</InfoLabel>
                                        <InfoValue>
                                          {subStock.supplier}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.ncm?.ncm && (
                                      <InfoItem>
                                        <InfoLabel>NCM:</InfoLabel>
                                        <InfoValue>{subRefs.ncm.ncm}</InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItem.weight && (
                                      <InfoItem>
                                        <InfoLabel>Peso (kg):</InfoLabel>
                                        <InfoValue>{subItem.weight}</InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItemCommonInfo}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'SERVICE' && (
                                <>
                                  {subItemTitle('🔧')}
                                  <InfoGrid>
                                    {subRefs.serviceCategory && (
                                      <InfoItem>
                                        <InfoLabel>Categoria:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.serviceCategory.name ||
                                            'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.serviceType && (
                                      <InfoItem>
                                        <InfoLabel>Tipo:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.serviceType.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.serviceSupplier && (
                                      <InfoItem>
                                        <InfoLabel>Fornecedor:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.serviceSupplier
                                            .companyName || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItemCommonInfo}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'RESOURCE' && (
                                <>
                                  {subItemTitle('🛠️')}
                                  <InfoGrid>
                                    {subRefs.resourceCategory && (
                                      <InfoItem>
                                        <InfoLabel>Categoria:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.resourceCategory.name ||
                                            'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.resourceType && (
                                      <InfoItem>
                                        <InfoLabel>Tipo:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.resourceType.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.resourceSupplier && (
                                      <InfoItem>
                                        <InfoLabel>Fornecedor:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.resourceSupplier
                                            .companyName || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItemCommonInfo}
                                  </InfoGrid>
                                </>
                              )}

                              {subItem.itemType === 'PRODUCT' && (
                                <>
                                  {subItemTitle('📦')}
                                  <InfoGrid>
                                    {subItem.nomeCustomizado && (
                                      <InfoItem>
                                        <InfoLabel>Especificação:</InfoLabel>
                                        <InfoValue>
                                          {subItem.nomeCustomizado}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subRefs.script && (
                                      <InfoItem>
                                        <InfoLabel>Script:</InfoLabel>
                                        <InfoValue>
                                          {subRefs.script.name || 'N/A'}
                                        </InfoValue>
                                      </InfoItem>
                                    )}
                                    {subItemCommonInfo}
                                  </InfoGrid>
                                </>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    <h5>💰 Subtotal</h5>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Peças:</InfoLabel>
                        <InfoValue>{item.quantityPieces ?? 'N/A'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Unidade:</InfoLabel>
                        <InfoValue>{unitName || 'N/A'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>NCM:</InfoLabel>
                        <InfoValue>{refs.ncm?.ncm || 'N/A'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Código:</InfoLabel>
                        <InfoValue>
                          {refs.product?.productCode || stock.code || 'N/A'}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Valor de venda:</InfoLabel>
                        <InfoValue>{formatCurrency(displayPrice)}</InfoValue>
                      </InfoItem>
                      {priceKgMin > 0 && (
                        <InfoItem>
                          <InfoLabel>Preço por Kg (Mín):</InfoLabel>
                          <InfoValue>{formatCurrency(priceKgMin)}</InfoValue>
                        </InfoItem>
                      )}
                      {priceKg > 0 && (
                        <InfoItem>
                          <InfoLabel>Preço por Kg (Final):</InfoLabel>
                          <InfoValue>{formatCurrency(priceKg)}</InfoValue>
                        </InfoItem>
                      )}
                      {discount > 0 && (
                        <InfoItem>
                          <InfoLabel>Desconto:</InfoLabel>
                          <InfoValue>{`${discount}%`}</InfoValue>
                        </InfoItem>
                      )}
                      <InfoItem>
                        <InfoLabel>
                          {hasBusinessDays
                            ? 'Prazo de entrega (dias úteis):'
                            : 'Prazo de entrega:'}
                        </InfoLabel>
                        <InfoValue>
                          {hasBusinessDays
                            ? item.deliveryBusinessDays
                            : item.time || 'N/A'}
                        </InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </ItemContent>
                )}
              </ItemCard>
            )
          })}

          <SectionTitle>Resumo Financeiro</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Quantidade Total:</InfoLabel>
              <InfoValue>{order?.totalQuantity || 0}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Impostos:</InfoLabel>
              <InfoValue>{formatCurrency(order?.taxes)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Total Geral:</InfoLabel>
              <InfoValue className='total-value'>
                {formatCurrency(order?.total)}
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          {Array.isArray((order as any)?.OrderInstallment) &&
            (order as any).OrderInstallment.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <InfoLabel style={{ display: 'block', marginBottom: '16px' }}>
                  Parcelas
                </InfoLabel>
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeaderCell width="64px">Nº</TableHeaderCell>
                      <TableHeaderCell>Valor</TableHeaderCell>
                      <TableHeaderCell>Vencimento</TableHeaderCell>
                      <TableHeaderCell width="160px">Comissão</TableHeaderCell>
                    </TableRow>
                  </thead>
                  <tbody>
                    {(order as any).OrderInstallment.map((parcela: any, index: number) => (
                      <TableRow key={parcela.id ?? index}>
                        <TableCell>{parcela.numero ?? index + 1}</TableCell>
                        <TableCell>{formatCurrency(Number(parcela.valor) || 0)}</TableCell>
                        <TableCell>{formatDate(parcela.vencimento)}</TableCell>
                        <TableCell>
                          {parcela.comissaoAtiva
                            ? `Sim${parcela.comissaoPercentual ? ` (${Number(parcela.comissaoPercentual)}%)` : ''}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
        </ScrollableContent>
      </DocumentContainer>
    </ModalOverlay>
  )
}

export default ViewSteelOrder
